import React from 'react';
import SEO from '../components/SEO';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Clock, Tag } from 'lucide-react';

const blogContent = {
  1: {
    title: "Winter 2025-2026 Fire Watch Readiness: Protecting Properties Nationwide",
    image: "https://images.unsplash.com/photo-1723399726195-1fc25450f80d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxzZWN1cml0eSUyMHBhdHJvbHxlbnwwfHx8fDE3Njg4OTI4MjJ8MA&ixlib=rb-4.1.0&q=85",
    date: "November 27, 2025",
    author: "Michael Roberts",
    category: "Fire Watch",
    readTime: "5 min read",
    content: `
      <p>As winter approaches, property managers and business owners across the nation must prepare for the increased risk of fire system impairments. Cold weather can affect sprinkler systems, fire pumps, and other fire suppression equipment, potentially requiring fire watch services to maintain compliance with fire codes.</p>
      
      <h2>Understanding Fire Watch Requirements</h2>
      <p>Fire watch is required whenever fire protection systems are impaired, out of service, or inadequate. During winter months, common triggers include:</p>
      <ul>
        <li>Frozen or damaged sprinkler pipes</li>
        <li>Fire pump maintenance or repairs</li>
        <li>Construction activities that impair fire systems</li>
        <li>Power outages affecting fire alarm systems</li>
      </ul>
      
      <h2>Preparing Your Property</h2>
      <p>Proactive preparation can help minimize fire watch requirements and associated costs:</p>
      <ul>
        <li>Inspect and maintain heating systems in sprinkler rooms</li>
        <li>Test antifreeze levels in dry pipe systems</li>
        <li>Ensure proper insulation of fire protection piping</li>
        <li>Review emergency procedures with staff</li>
      </ul>
      
      <h2>Our Fire Watch Services</h2>
      <p>Armed Security Service provides 24-hour fire watch guards who are:</p>
      <ul>
        <li>Trained in fire prevention and detection</li>
        <li>Equipped with proper communication devices</li>
        <li>Familiar with OSHA and local fire code requirements</li>
        <li>Available for immediate deployment</li>
      </ul>
      
      <p>Don't wait until an emergency occurs. Contact us today to discuss your fire watch needs and develop a winter preparedness plan for your property.</p>
    `
  },
  2: {
    title: "Event Security Contract Costs: How Major Venues Budget Per Event",
    image: "https://images.unsplash.com/photo-1552739883-6578dc150ac6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwzfHxhcm1lZCUyMGd1YXJkfGVufDB8fHx8MTc2ODg5MjgzNHww&ixlib=rb-4.1.0&q=85",
    date: "November 22, 2025",
    author: "Sarah Chen",
    category: "Event Security",
    readTime: "8 min read",
    content: `
      <p>Seven hours before doors open, the command center is already buzzing. Twenty monitors show empty seats, loading docks, and perimeter fences. The security director reviews the deployment plan one final time, knowing that in just a few hours, 50,000 people will flood through those gates.</p>
      
      <h2>Understanding Event Security Costs</h2>
      <p>Event security budgets vary dramatically based on several factors:</p>
      <ul>
        <li>Expected attendance and crowd density</li>
        <li>Type of event and associated risks</li>
        <li>Venue layout and access points</li>
        <li>Local regulations and permit requirements</li>
        <li>Level of VIP protection needed</li>
      </ul>
      
      <h2>Breaking Down the Budget</h2>
      <p>For a typical large-scale event, security costs typically include:</p>
      <ul>
        <li><strong>Personnel:</strong> 50-60% of total budget</li>
        <li><strong>Equipment and technology:</strong> 15-20%</li>
        <li><strong>Planning and coordination:</strong> 10-15%</li>
        <li><strong>Contingency:</strong> 10-15%</li>
      </ul>
      
      <h2>Value Over Price</h2>
      <p>While cost is important, experienced event managers understand that security failures can be far more expensive than the security itself. A single incident can result in:</p>
      <ul>
        <li>Legal liability and lawsuits</li>
        <li>Insurance premium increases</li>
        <li>Reputation damage</li>
        <li>Regulatory penalties</li>
      </ul>
      
      <p>Contact Armed Security Service for a customized event security quote. Our team has secured thousands of events nationwide, from intimate corporate gatherings to major concerts and sporting events.</p>
    `
  }
};

const BlogDetailPage = () => {
  const { postId } = useParams();
  const post = blogContent[postId] || blogContent[1];

  const relatedPosts = [
    { id: 3, title: "Executive Protection Pricing", category: "Executive Protection" },
    { id: 4, title: "Construction Site Security Tips", category: "Commercial Security" },
    { id: 5, title: "Technology in Security Operations", category: "Industry Trends" }
  ];

  return (
    <>
      <SEO 
        title={post.title}
        description={post.excerpt}
        keywords={`security blog, ${post.title}, security articles, security tips`}
      />
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
          <span className="inline-block bg-orange-500/20 text-orange-500 px-3 py-1 rounded-full text-sm font-medium mb-4">
            {post.category}
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-gray-400">
            <span className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {post.date}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </span>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
          />
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-3">
              <article 
                className="prose prose-invert prose-lg max-w-none
                  prose-headings:text-white prose-headings:font-bold prose-headings:mt-8 prose-headings:mb-4
                  prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-6
                  prose-ul:text-gray-300 prose-ul:my-4
                  prose-li:mb-2
                  prose-strong:text-white"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
              
              {/* Share */}
              <div className="mt-12 pt-8 border-t border-slate-800">
                <p className="text-white font-semibold mb-4">Share this article:</p>
                <div className="flex gap-3">
                  {[Facebook, Twitter, Linkedin].map((Icon, index) => (
                    <button 
                      key={index}
                      className="w-10 h-10 bg-slate-800 rounded-full flex items-center justify-center text-gray-400 hover:bg-orange-500 hover:text-white transition-all"
                    >
                      <Icon className="w-5 h-5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-32">
                <h3 className="text-white font-bold mb-4">Related Articles</h3>
                <div className="space-y-4">
                  {relatedPosts.map((related) => (
                    <Link 
                      key={related.id}
                      to={`/blog/${related.id}`}
                      className="block bg-slate-800 rounded-lg p-4 hover:bg-slate-750 transition-colors"
                    >
                      <span className="text-orange-500 text-xs">{related.category}</span>
                      <h4 className="text-white text-sm font-medium mt-1">{related.title}</h4>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default BlogDetailPage;